import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can, formatCode, writeOffOtherSiteMessage, writeOffSiteBlocked, writeOffStateProblem } from '@pl/domain';
import type { BoxDetailDto, BoxState, WriteOffReason } from '@pl/domain';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../../lib/api';
import { eventLine, eventTitle, FOUND_WORDS, type FoundReason, WRITE_OFF_WORDS } from '../../lib/boxHistory';
import { stateWords, zonedDateTime } from '../../lib/format';
import { qk, useBox, useMe, useOrg, useSiteMap } from '../../lib/queries';
import { ErrorNote, RefetchNote, SiteTag, Spinner, Swatch } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { Modal } from '../../ui/Dialog';
import { QrCode } from '../../ui/QrCode';
import s from './BoxLookup.module.css';

const NODE_TONE: Partial<Record<string, 'ok' | 'bad' | 'accent'>> = {
  'box.received': 'ok',
  'box.written_off': 'bad',
  'box.picked': 'accent',
};

const STATE_TONE: Partial<Record<BoxState, 'ok' | 'accent' | 'ink' | 'warn' | 'bad'>> = {
  in_stock: 'ok',
  picked: 'accent',
  shipped: 'ink',
  in_transit: 'warn',
  written_off: 'bad',
};

const WRITE_OFF_REASONS = (Object.keys(WRITE_OFF_WORDS) as WriteOffReason[]).map((value) => ({ value, label: WRITE_OFF_WORDS[value] }));
const FOUND_REASONS = (Object.keys(FOUND_WORDS) as FoundReason[]).map((value) => ({ value, label: FOUND_WORDS[value] }));

function BoxSearchForm({ value, onChange, onSubmit }: { value: string; onChange: (v: string) => void; onSubmit: (e: FormEvent) => void }) {
  return (
    <div className={s.centered}>
      <form className={`panel ${s.searchCard}`} onSubmit={onSubmit}>
        <div className="panel-h">
          <h2>Find a box</h2>
        </div>
        <div className="panel-b">
          <label className="fld">
            <span>Box code</span>
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <input className="inp" autoFocus value={value} onChange={(e) => onChange(e.target.value)} placeholder="B7K3F-9QX2M" />
          </label>
          <button type="submit" className="btn primary">
            Look up
          </button>
        </div>
      </form>
    </div>
  );
}

export function BoxLookup() {
  const { code } = useParams<{ code?: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const siteMap = useSiteMap();
  const orgQuery = useOrg();
  const boxQuery = useBox(code);
  const [searchValue, setSearchValue] = useState('');
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  // No reason is chosen for the lead: a write-off records what they picked, never a default.
  const [writeOffReason, setWriteOffReason] = useState<WriteOffReason | null>(null);
  const [writeOffNote, setWriteOffNote] = useState('');
  const [foundOpen, setFoundOpen] = useState(false);
  const [foundSiteId, setFoundSiteId] = useState('');
  const [foundReason, setFoundReason] = useState<FoundReason>('found_here');
  const [foundNote, setFoundNote] = useState('');
  const [reprintError, setReprintError] = useState<string | null>(null);
  const [reprintFallbackUrl, setReprintFallbackUrl] = useState<string | null>(null);
  const reprintFallbackRef = useRef<string | null>(null);
  reprintFallbackRef.current = reprintFallbackUrl;

  // Revoke any still-pending fallback blob URL if the page is left before it's opened.
  useEffect(() => () => {
    if (reprintFallbackRef.current) URL.revokeObjectURL(reprintFallbackRef.current);
  }, []);

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    const v = searchValue.trim();
    if (!v) return;
    nav(`/office/boxes/${encodeURIComponent(v)}`);
  }

  function invalidateAfterAction() {
    if (code) qc.invalidateQueries({ queryKey: qk.box(code) });
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'stock' || q.queryKey[0] === 'stock-boxes' });
  }

  const writeOff = useMutation({
    mutationFn: (body: { reason: string; note: string | null }) => api.post<BoxDetailDto>(`/boxes/${code}/write-off`, body),
    onSuccess: () => {
      invalidateAfterAction();
      setWriteOffOpen(false);
      setWriteOffNote('');
    },
  });

  const found = useMutation({
    mutationFn: (body: { siteId: string; reason: string; note: string | null }) => api.post<BoxDetailDto>(`/boxes/${code}/found`, body),
    onSuccess: () => {
      invalidateAfterAction();
      setFoundOpen(false);
      setFoundNote('');
    },
  });

  async function reprint() {
    if (!code) return;
    setReprintError(null);
    if (reprintFallbackUrl) {
      URL.revokeObjectURL(reprintFallbackUrl);
      setReprintFallbackUrl(null);
    }
    try {
      const blob = await api.pdf(`/boxes/${code}/reprint`, 'POST');
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) {
        URL.revokeObjectURL(url);
      } else {
        // Popup blocked (window.open returns null without throwing) — offer a real link instead,
        // and keep the blob URL alive until it's clicked, replaced by a later reprint, or the page unmounts.
        setReprintFallbackUrl(url);
      }
    } catch (e) {
      setReprintError(e instanceof Error ? e.message : 'Could not reprint this sticker');
    }
  }

  if (!code) {
    return <BoxSearchForm value={searchValue} onChange={setSearchValue} onSubmit={submitSearch} />;
  }

  if (boxQuery.isPending) {
    return (
      <div className="row">
        <Spinner label="Loading box" />
      </div>
    );
  }

  // A box whose first load failed has nothing to show. One that is on screen and fails a refresh —
  // after a write-off, say — stays on screen, with a RefetchNote above it.
  if (boxQuery.isLoadingError) {
    return (
      <div className={s.page}>
        <ErrorNote error={boxQuery.error} />
        <BoxSearchForm value={searchValue} onChange={setSearchValue} onSubmit={submitSearch} />
      </div>
    );
  }

  const box = boxQuery.data;
  const role = me.data?.role;
  const canWriteOff = Boolean(role && can(role, 'write_off'));
  const canFound = Boolean(role && can(role, 'found'));
  const canLabels = Boolean(role && can(role, 'labels'));
  const site = box.siteId ? siteMap.get(box.siteId) : undefined;
  // Every time on this page is read at the box's site; a box no site holds yet reads at the company's.
  const zone = site?.timezone ?? orgQuery.data?.timezone;
  const orderLabel = box.order ? (box.order.externalRef ?? box.order.code) : null;
  const history = [...box.history].sort((a, b) => b.id - a.id);
  const userSites = me.data?.sites ?? [];

  const writeOffProblem = writeOffStateProblem(box.state);
  // A lead acts on boxes at their own sites only. Found / return stays: marking a box found here
  // is how a lead fixes one recorded at another site that is physically at theirs.
  const writeOffElsewhere = canWriteOff && writeOffSiteBlocked(box, me.data?.siteIds ?? null);

  function openWriteOff() {
    setWriteOffReason(null);
    setWriteOffNote('');
    writeOff.reset();
    setWriteOffOpen(true);
  }

  function openFound() {
    setFoundSiteId(userSites[0]?.id ?? '');
    setFoundReason('found_here');
    setFoundNote('');
    setFoundOpen(true);
  }

  return (
    <div className={s.page}>
      <RefetchNote queries={[boxQuery]} />
      <div className={`panel ${s.hero}`}>
        <div className={s.qrbox}>
          <QrCode value={box.code} size={112} />
        </div>

        <div className={s.id}>
          <span className="k">Box</span>
          <div className={s.codeXl}>{formatCode(box.code)}</div>
          <div className={s.skuXl}>
            <Swatch hex={box.sku?.colourHex ?? null} name={box.sku?.colourName ?? null} size="xl" />
            <b>{box.sku?.name ?? 'Blank sticker'}</b>
            {box.sku?.colourName && <span className="clr">{box.sku.colourName}</span>}
            {box.sku?.code && <span className="code">{box.sku.code}</span>}
          </div>
          <div className={s.chips}>
            <span className={`chip ${STATE_TONE[box.state] ?? ''} ${s.chipLg}`.trim()}>
              {stateWords(box.state)}
              {orderLabel && (
                <>
                  {' · '}
                  <span className="mono">{orderLabel}</span>
                </>
              )}
            </span>
            {site && (
              <span className={`chip ${s.chipLg}`}>
                <SiteTag site={site} />
                {site.name}
              </span>
            )}
          </div>
        </div>

        <div className={s.acts}>
          <div className="row">
            {box.order && (
              <Link to={`/office/orders/${box.order.id}`} className="btn sm">
                <Icon name="orders" />
                Open <span className="mono">{orderLabel}</span>
              </Link>
            )}
            <button type="button" className="btn sm" disabled={!canLabels} onClick={reprint}>
              <Icon name="printer" />
              Reprint sticker
            </button>
          </div>
          {reprintError && <ErrorNote error={new Error(reprintError)} />}
          {reprintFallbackUrl && (
            <p className="sub">
              Your browser blocked the popup.{' '}
              <a href={reprintFallbackUrl} target="_blank" rel="noreferrer">
                Open the PDF
              </a>
            </p>
          )}
          <span className="k">
            Lead actions
            {(!canWriteOff || !canFound) && (
              <span className="lead">
                <Icon name="lock" />
                Lead only
              </span>
            )}
          </span>
          <div className={s.leadRow}>
            {!writeOffElsewhere && (
              <button type="button" className={`btn sm${canWriteOff ? '' : ' off'}`} disabled={!canWriteOff || Boolean(writeOffProblem)} onClick={openWriteOff}>
                <Icon name="trash" />
                Write off
              </button>
            )}
            <button type="button" className={`btn sm${canFound ? '' : ' off'}`} disabled={!canFound} onClick={openFound}>
              <Icon name="undo" />
              Found / return
            </button>
          </div>
          {(!canWriteOff || !canFound) && <div className="tip">Lead only</div>}
          {writeOffElsewhere && <p className="sub">{writeOffOtherSiteMessage(site?.name ?? 'another site')}</p>}
          {canWriteOff && !writeOffElsewhere && writeOffProblem && <p className="sub">{writeOffProblem}</p>}
        </div>
      </div>

      <div className={s.cols}>
        <div className="panel">
          <div className="panel-h">
            <h2>History</h2>
            <span className="chip">{history.length} events</span>
            <span className="grow" />
            <span className="sub">Newest first</span>
          </div>
          <ul className="tl">
            {history.map((ev, i) => (
              <li key={ev.id}>
                <div className="when">
                  <b>{zonedDateTime(ev.at, zone)}</b>
                </div>
                <span className={`node${NODE_TONE[ev.type] ? ` ${NODE_TONE[ev.type]}` : ''}`} />
                <div className={s.ev}>
                  <h3>
                    {eventTitle(ev)}
                    {ev.actorName && <span> · {ev.actorName}</span>}
                    {i === 0 && <span className={s.now}>Now</span>}
                  </h3>
                  <p>{eventLine(ev) || '—'}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className={s.rcol}>
          <div className="panel">
            <div className="panel-h">
              <h2>Facts</h2>
            </div>
            <div className="facts">
              <div>
                <span className="k">SKU code</span>
                <span className="v mono">{box.sku?.code ?? '—'}</span>
              </div>
              <div>
                <span className="k">Site</span>
                <span className="v">
                  {site ? (
                    <>
                      <SiteTag site={site} />
                      {site.name}
                    </>
                  ) : (
                    '—'
                  )}
                </span>
              </div>
              <div>
                <span className="k">Pallet</span>
                <span className="v mono">{box.pallet ? formatCode(box.pallet.code) : '—'}</span>
              </div>
              <div>
                <span className="k">Sticker batch</span>
                <span className="v mono">{box.batch.code}</span>
              </div>
              <div>
                <span className="k">Date received</span>
                <span className="v">{box.receivedAt ? zonedDateTime(box.receivedAt, zone) : 'Not received'}</span>
              </div>
              <div style={{ borderBottom: 0 }}>
                <span className="k">Current order</span>
                <span className="v mono">{orderLabel ?? '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={writeOffOpen}
        onOpenChange={setWriteOffOpen}
        title="Write off box"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setWriteOffOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={writeOff.isPending || !writeOffReason || (writeOffReason === 'other' && !writeOffNote.trim())}
              onClick={() => writeOffReason && writeOff.mutate({ reason: writeOffReason, note: writeOffNote.trim() ? writeOffNote.trim() : null })}
            >
              Write off box
            </button>
          </>
        }
      >
        {writeOff.error && <ErrorNote error={writeOff.error} />}
        <p className="sub">{writeOffReason ? 'Reason' : 'Choose a reason'}</p>
        <div className="seg" role="group" aria-label="Reason">
          {WRITE_OFF_REASONS.map((r) => (
            <button key={r.value} type="button" className={writeOffReason === r.value ? 'on' : undefined} aria-pressed={writeOffReason === r.value} onClick={() => setWriteOffReason(r.value)}>
              {r.label}
            </button>
          ))}
        </div>
        <label className="fld">
          <span>Note{writeOffReason === 'other' ? ' (required)' : ' (optional)'}</span>
          <textarea className="inp" value={writeOffNote} onChange={(e) => setWriteOffNote(e.target.value)} rows={3} />
        </label>
      </Modal>

      <Modal
        open={foundOpen}
        onOpenChange={setFoundOpen}
        title="Found / return box"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setFoundOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={found.isPending || !foundSiteId}
              onClick={() => found.mutate({ siteId: foundSiteId, reason: foundReason, note: foundNote.trim() ? foundNote.trim() : null })}
            >
              Mark in stock
            </button>
          </>
        }
      >
        {found.error && <ErrorNote error={found.error} />}
        <label className="fld">
          <span>Site</span>
          <select className="inp" value={foundSiteId} onChange={(e) => setFoundSiteId(e.target.value)}>
            {userSites.map((st) => (
              <option key={st.id} value={st.id}>
                {st.name}
              </option>
            ))}
          </select>
        </label>
        <div className="seg">
          {FOUND_REASONS.map((r) => (
            <button key={r.value} type="button" className={foundReason === r.value ? 'on' : undefined} onClick={() => setFoundReason(r.value)}>
              {r.label}
            </button>
          ))}
        </div>
        <label className="fld">
          <span>Note (optional)</span>
          <textarea className="inp" value={foundNote} onChange={(e) => setFoundNote(e.target.value)} rows={3} />
        </label>
      </Modal>
    </div>
  );
}
